import type {
  Ticket,
  TicketTag,
  TicketComment,
  TicketStatus,
} from "@/types/ticket";
import type { User } from "@/types/user";
import type { Contact } from "@/features/contact/types";

import { faker } from "@faker-js/faker";

import {
  createId,
  createPastTimestamp,
  pickRandom,
  pickMultiple,
  randomBool,
  randomInt,
} from "./base";

const TICKET_NAMES = [
  "Appointment Rescheduling Request",
  "Insurance Verification Needed",
  "Payment Plan Setup",
  "Medical Records Request",
  "Follow-up Call Required",
  "Treatment Plan Review",
  "Patient Complaint",
  "Referral Processing",
  "Emergency Appointment",
  "Billing Inquiry",
  "New Patient Registration",
  "Lab Results Review",
  "Pre-Authorization Request",
  "Appointment Confirmation",
  "Patient Feedback",
];

const TICKET_TAG_NAMES = [
  { name: "Urgent", color: "#FF4444" },
  { name: "Insurance", color: "#4ECDC4" },
  { name: "Billing", color: "#FFD700" },
  { name: "Scheduling", color: "#87CEEB" },
  { name: "Medical", color: "#98D8C8" },
];

const TICKET_STATUSES: TicketStatus[] = [
  "pending",
  "progress",
  "resolved",
  "pending-action",
];

/**
 * Generate ticket tags
 */
export function generateTicketTags(): TicketTag[] {
  return TICKET_TAG_NAMES.map((config) => ({
    id: createId(),
    name: config.name,
    color: config.color,
  }));
}

/**
 * Generate mock tickets
 */
export function generateTickets(
  organizationId: string,
  workspaceId: string,
  users: User[],
  contacts: Contact[],
  count: number = 15,
): Ticket[] {
  const tickets: Ticket[] = [];
  const ticketTags = generateTicketTags();

  for (let i = 0; i < count; i++) {
    const creator = pickRandom(users);
    const assignee = randomBool(0.8) ? pickRandom(users) : null;
    const contact = randomBool(0.7) ? pickRandom(contacts) : null;
    const status = pickRandom(TICKET_STATUSES);
    const timestamp = createPastTimestamp(randomInt(1, 30));

    // Generate 0-3 tags
    const tags = pickMultiple(ticketTags, 0, 3);

    // Generate 0-5 comments
    const commentCount = randomInt(0, 5);
    const comments: TicketComment[] = [];

    for (let j = 0; j < commentCount; j++) {
      const commenter = pickRandom(users);

      comments.push({
        id: createId(),
        text: faker.lorem.sentence(),
        ticketId: "", // Will be set after ticket ID is created
        user: {
          id: commenter.id,
          firstName: commenter.firstName,
          lastName: commenter.lastName,
        },
        createdAt: createPastTimestamp(randomInt(0, 10)),
        updatedAt: null,
      });
    }

    const ticketId = createId();

    // Update comment ticketIds
    comments.forEach((c) => {
      c.ticketId = ticketId;
    });

    tickets.push({
      id: ticketId,
      name: TICKET_NAMES[i % TICKET_NAMES.length],
      description: faker.lorem.paragraph(),
      createdBy: creator.id,
      assignedTo: assignee?.id ?? null,
      status,
      order: i,
      contactId: contact?.id ?? null,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: null,
      deletedAt: null,
      tags,
      comments,
    });
  }

  return tickets;
}
